import React, { useState } from 'react';
import { X, Upload, Loader2, Check, Star, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { adminAPI } from '../services/api';

/** Admin: import existing WooCommerce product reviews (CSV export or WC REST products/reviews JSON) → preview → apply. */
const WooReviewsImportModal = ({ onClose, onDone }) => {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [consent, setConsent] = useState(false);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try { setPreview(await adminAPI.wooReviewsPreview(f)); } catch (err) { toast.error(err.response?.data?.detail || 'Fichier illisible'); } finally { setBusy(false); }
  };
  const apply = async () => {
    setBusy(true);
    try {
      const r = await adminAPI.wooReviewsApply(preview.rows);
      toast.success(r.message);
      onDone();
      onClose();
    } catch (err) { toast.error(err.response?.data?.detail || 'Import impossible'); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="woo-reviews-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-5 border-b bg-violet-700 text-white flex items-center justify-between">
          <h2 className="text-lg font-bold inline-flex items-center gap-2"><Upload size={20} /> Importer les avis WooCommerce</h2>
          <button type="button" onClick={onClose} className="text-white/80 hover:text-white" data-testid="woo-reviews-close"><X size={22} /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-6 space-y-4 text-sm">
          <p className="text-xs text-gray-600 bg-violet-50 border border-violet-200 rounded-lg p-3">
            Formats acceptés : <strong>CSV</strong> d'export des avis WooCommerce (colonnes type <code>product_sku</code>/<code>SKU</code>, <code>comment_author</code>, <code>comment_date</code>, <code>comment_content</code>, <code>rating</code>, <code>verified</code>) ou <strong>JSON</strong> de l'API <code>/wp-json/wc/v3/products/reviews</code>. Les produits sont retrouvés par <strong>SKU = référence</strong> (ou nom exact). Les avis importés portent la mention « recueilli par le marchand » ; « Achat vérifié » seulement si WooCommerce l'indiquait.
          </p>
          <label className="flex items-center justify-center gap-2 border-2 border-dashed border-gray-300 rounded-lg p-6 cursor-pointer hover:border-violet-400 text-gray-600">
            {busy ? <Loader2 size={18} className="animate-spin" /> : <Upload size={18} />} {preview ? 'Choisir un autre fichier' : 'Choisir le fichier CSV ou JSON'}
            <input type="file" accept=".csv,.json,text/csv,application/json" className="hidden" onChange={onFile} data-testid="woo-reviews-file" />
          </label>
          {preview && (
            <>
              <div className="flex flex-wrap gap-2 text-xs" data-testid="woo-reviews-summary">
                <span className="bg-gray-100 rounded-md px-2 py-1">{preview.total} avis lus</span>
                <span className="bg-green-100 text-green-800 rounded-md px-2 py-1" data-testid="woo-reviews-importable">{preview.importable} importables</span>
                <span className="bg-gray-100 rounded-md px-2 py-1">{preview.products_matched} produit(s) reconnus</span>
                {preview.duplicates > 0 && <span className="bg-amber-100 text-amber-800 rounded-md px-2 py-1">{preview.duplicates} déjà importés</span>}
                {preview.rejected > 0 && <span className="bg-red-100 text-red-800 rounded-md px-2 py-1">{preview.rejected} rejetés</span>}
              </div>
              <div className="border rounded-lg overflow-hidden max-h-72 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 text-gray-500 sticky top-0"><tr><th className="text-left px-3 py-2">Produit</th><th className="text-left px-2 py-2">Client</th><th className="px-2 py-2">Note</th><th className="text-left px-2 py-2">Avis</th><th className="text-left px-2 py-2">Statut</th></tr></thead>
                  <tbody className="divide-y divide-gray-100">
                    {preview.rows.map((r, i) => (
                      <tr key={i} className={r.ok ? '' : 'bg-red-50/50 text-gray-500'} data-testid={`woo-review-row-${i}`}>
                        <td className="px-3 py-1.5 max-w-[200px] truncate" title={r.product_label || r.sku || r.product_name}>{r.product_label || <span className="text-red-600">{r.sku || r.product_name || '—'}</span>}</td>
                        <td className="px-2 py-1.5 whitespace-nowrap">{r.author}</td>
                        <td className="px-2 py-1.5 text-center whitespace-nowrap"><Star size={11} className="inline fill-yellow-400 text-yellow-400" /> {r.rating}</td>
                        <td className="px-2 py-1.5 max-w-[260px] truncate" title={r.content}>{r.content}</td>
                        <td className="px-2 py-1.5">{r.ok ? <span className="text-green-700 inline-flex items-center gap-1"><Check size={11} /> OK{r.problems.length ? ` · ${r.problems.join(', ')}` : ''}</span> : <span className="text-red-700 inline-flex items-center gap-1"><AlertTriangle size={11} /> {r.problems.join(', ')}</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <label className="flex items-start gap-2 text-xs text-gray-700 cursor-pointer bg-gray-50 border rounded-lg p-3">
                <input type="checkbox" data-testid="woo-reviews-consent" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 rounded border-gray-300 text-violet-600" />
                <span>Je confirme que ces avis ont été déposés par de vrais clients de mon ancienne boutique WooCommerce et sont retranscrits sans modification.</span>
              </label>
            </>
          )}
        </div>
        <div className="p-4 border-t bg-gray-50 flex justify-end gap-3">
          <Button variant="outline" onClick={onClose} data-testid="woo-reviews-cancel">Annuler</Button>
          <Button onClick={apply} disabled={busy || !preview || !preview.importable || !consent} className="bg-violet-700 hover:bg-violet-800" data-testid="woo-reviews-apply">
            {busy ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Check size={16} className="mr-2" />} Importer {preview?.importable || 0} avis
          </Button>
        </div>
      </div>
    </div>
  );
};

export default WooReviewsImportModal;
